import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { reportsAPI, followupsAPI, revenueAPI, commissionAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useRole } from '../hooks/useRole';
import { Card, Spinner, StageBadge, PriorityBadge, PageHeader } from '../components/ui';
import { STAGE_COLORS } from '../utils/constants';
import toast from 'react-hot-toast';
import MyTasksWidget from '../components/tasks/MyTasksWidget';

const StatCard = ({ label, value, icon, color = '#1B2A6B', link }) => {
  const inner = (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', height: '100%' }}>
      <div style={{ width: 48, height: 48, background: `${color}15`, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>{icon}</div>
      <div>
        <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</p>
        <p style={{ fontSize: 28, fontWeight: 800, color, marginTop: 2 }}>{value ?? '—'}</p>
      </div>
    </div>
  );
  return link ? <Link to={link} style={{ textDecoration: 'none', display: 'block' }}>{inner}</Link> : inner;
};

const FunnelBar = ({ stage, count, total }) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  const c = STAGE_COLORS[stage] || { bg: '#f1f5f9', text: '#64748b' };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
      <div style={{ width: 110, fontSize: 12, fontWeight: 600, color: '#475569', flexShrink: 0 }}>{stage}</div>
      <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 4, height: 20, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, background: c.text, height: '100%', borderRadius: 4, transition: 'width 0.5s ease', display: 'flex', alignItems: 'center', paddingLeft: 6 }}>
          {pct > 8 && <span style={{ fontSize: 10, fontWeight: 700, color: '#fff' }}>{count}</span>}
        </div>
      </div>
      <div style={{ width: 30, fontSize: 12, fontWeight: 700, color: '#334155', textAlign: 'right', flexShrink: 0 }}>{count}</div>
    </div>
  );
};

export default function DashboardPage() {
  const { user } = useAuth();
  const { isAdminOrAbove, canViewAllData, canViewRegionData, isRCM, isManager, isIntern, isSupport } = useRole();
  const isAdmin = isAdminOrAbove;
  const canViewAllLeads = canViewAllData || canViewRegionData;
  const [data, setData] = useState(null);
  const [overdue, setOverdue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revDash, setRevDash] = useState(null);
  const [myEarnings, setMyEarnings] = useState(null);
  const [myTarget, setMyTarget] = useState(null);

  useEffect(() => {
    const calls = [
      reportsAPI.getDashboard(),
      followupsAPI.getOverdue(),
    ];
    const canSeeRevenueDash = isAdminOrAbove || canViewRegionData || isRCM || isManager;
    if (canSeeRevenueDash) calls.push(revenueAPI.getDashboard().catch(() => null));
    calls.push(revenueAPI.getMyEarnings().catch(() => null));

    Promise.all(calls).then(([dRes, oRes, ...rest]) => {
      setData(dRes.data?.data);
      setOverdue(oRes.data?.data || []);
      const canSeeRevenueDash = isAdminOrAbove || canViewRegionData || isRCM || isManager;
      if (canSeeRevenueDash && rest[0]) { setRevDash(rest[0]?.data?.data); setMyEarnings(rest[1]?.data?.data); }
      else { setMyEarnings(rest[0]?.data?.data); }
    }).catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [user]);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
      <Spinner size={40} />
    </div>
  );

  const s = data?.summary || {};

  return (
    <div>
      <PageHeader
        title={`Good ${new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}, ${user?.name?.split(' ')[0]} 👋`}
        subtitle="Here's what's happening at Nova Era Labs today"
      />

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Total Leads" value={s.totalLeads} icon="🎯" color="#1B2A6B" link="/leads" />
        <StatCard label="Active Pipeline" value={s.activePipeline} icon="📈" color="#059669" />
        <StatCard label="Closed This Month" value={s.closedThisMonth} icon="🏆" color="#C9A84C" />
        <StatCard label="Overdue Follow-ups" value={overdue.length} icon="⚠️" color="#ef4444" link="/leads?filter=overdue" />
        {isAdmin && <StatCard label="Stale Leads (7d)" value={s.staleLeads} icon="💤" color="#f59e0b" />}
        {isAdmin && <StatCard label="Team Members" value={s.teamCount} icon="👥" color="#7c3aed" link="/team" />}
      </div>

      {/* My Tasks Widget */}
      <div style={{ marginBottom: 24 }}>
        <MyTasksWidget />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: canViewAllLeads ? '1fr 1fr' : '1fr', gap: 20, marginBottom: 24 }}>
        {/* Stage Funnel */}
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: '#1B2A6B' }}>Pipeline Funnel</h2>
            <Link to="/leads" style={{ fontSize: 12, color: '#C9A84C', fontWeight: 600, textDecoration: 'none' }}>View all →</Link>
          </div>
          {data?.leadsByStage?.length ? data.leadsByStage.map(item => (
            <FunnelBar key={item._id} stage={item._id} count={item.count} total={s.totalLeads} />
          )) : <p style={{ color: '#94a3b8', fontSize: 13 }}>No pipeline data yet</p>}
        </Card>

        {/* Lead Source */}
        {canViewAllLeads && (
          <Card>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: '#1B2A6B', marginBottom: 16 }}>Leads by Source</h2>
            {data?.leadsBySource?.map(item => (
              <div key={item._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: 13, color: '#475569', textTransform: 'capitalize' }}>{item._id?.replace('_', ' ')}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 80, background: '#f1f5f9', borderRadius: 4, height: 6 }}>
                    <div style={{ width: `${(item.count / (s.totalLeads || 1)) * 100}%`, background: '#1B2A6B', height: '100%', borderRadius: 4 }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#1B2A6B', width: 24, textAlign: 'right' }}>{item.count}</span>
                </div>
              </div>
            ))}
          </Card>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isAdmin ? '1fr 1fr' : '1fr', gap: 20 }}>
        {/* Overdue Follow-ups */}
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: '#1B2A6B' }}>⚠️ Overdue Follow-ups</h2>
            <span style={{ background: '#fee2e2', color: '#dc2626', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{overdue.length}</span>
          </div>
          {overdue.length === 0 ? (
            <p style={{ color: '#22c55e', fontSize: 13, textAlign: 'center', padding: '12px 0' }}>✅ All follow-ups are up to date!</p>
          ) : (
            <div style={{ maxHeight: 240, overflowY: 'auto' }}>
              {overdue.slice(0, 10).map(f => (
                <Link key={f._id} to={`/leads/${f.lead_id?._id || f.lead_id}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9', textDecoration: 'none' }}>
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#1B2A6B' }}>{f.lead_id?.customer_type || 'Lead'}</p>
                    <p style={{ fontSize: 11, color: '#94a3b8' }}>{f.type} · {new Date(f.scheduled_at).toLocaleDateString()}</p>
                  </div>
                  <span style={{ fontSize: 11, background: '#fee2e2', color: '#dc2626', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>OVERDUE</span>
                </Link>
              ))}
            </div>
          )}
        </Card>

        {/* Team Performance (admin only) */}
        {isAdmin && data?.teamPerformance && (
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: '#1B2A6B' }}>👥 Team Performance</h2>
              <Link to="/team" style={{ fontSize: 12, color: '#C9A84C', fontWeight: 600, textDecoration: 'none' }}>Manage →</Link>
            </div>
            <div style={{ maxHeight: 240, overflowY: 'auto' }}>
              {data.teamPerformance.slice(0, 8).map(member => (
                <div key={member._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ width: 32, height: 32, background: '#e0e7ff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#1B2A6B', flexShrink: 0 }}>
                    {member.name?.[0]}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member.name}</p>
                    <p style={{ fontSize: 11, color: '#94a3b8' }}>{member.sub_role}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: 14, fontWeight: 800, color: '#1B2A6B' }}>{member.leadCount}</p>
                    <p style={{ fontSize: 10, color: '#94a3b8' }}>leads</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* My Today's Tasks */}
        {!isAdmin && data?.myTasks && (
          <Card>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: '#1B2A6B', marginBottom: 14 }}>📋 Today's Tasks</h2>
            {data.myTasks.length === 0 ? (
              <p style={{ color: '#94a3b8', fontSize: 13 }}>No tasks for today</p>
            ) : data.myTasks.map(task => (
              <div key={task._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: task.priority === 'High' ? '#ef4444' : task.priority === 'Medium' ? '#f59e0b' : '#22c55e', flexShrink: 0 }} />
                <p style={{ fontSize: 13, color: '#334155', flex: 1 }}>{task.title}</p>
                <PriorityBadge priority={task.priority} />
              </div>
            ))}
          </Card>
        )}

        {/* Revenue Summary Widget (Admin/SVP/MD) */}
        {revDash && (
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: '#1B2A6B' }}>💰 Revenue Summary</h2>
              <Link to="/revenue" style={{ fontSize: 12, color: '#C9A84C', fontWeight: 600, textDecoration: 'none' }}>View All →</Link>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div style={{ padding: '10px 14px', background: '#f0fdf4', borderRadius: 8 }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>This Month</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#15803d' }}>
                  ₹{((revDash.total_revenue_month || 0) / 100000).toFixed(1)}L
                </div>
              </div>
              <div style={{ padding: '10px 14px', background: '#eff6ff', borderRadius: 8 }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>This Quarter</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#1d4ed8' }}>
                  ₹{((revDash.total_revenue_quarter || 0) / 100000).toFixed(1)}L
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 13 }}>
              <span style={{ color: '#374151' }}><strong>{revDash.enrollments_month || 0}</strong> Enrollments</span>
              <span style={{ color: '#374151' }}><strong>{revDash.deals_month || 0}</strong> Deals</span>
            </div>
          </Card>
        )}

        {/* My Earnings Widget (all roles) */}
        {myEarnings && myEarnings.entries?.length > 0 && (
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: '#1B2A6B' }}>📈 My Earnings This Month</h2>
              <Link to="/my-earnings" style={{ fontSize: 12, color: '#C9A84C', fontWeight: 600, textDecoration: 'none' }}>Details →</Link>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#6366f1' }}>
              ₹{(myEarnings.entries.reduce((s, e) => s + (e.commission_amount || 0), 0)).toLocaleString('en-IN')}
            </div>
            {myEarnings.target && (
              <div style={{ marginTop: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                  <span>Target Progress</span>
                  <span>{myEarnings.target.achievement_percentage || 0}%</span>
                </div>
                <div style={{ height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(100, myEarnings.target.achievement_percentage || 0)}%`, background: '#6366f1', borderRadius: 4 }} />
                </div>
                <div style={{ marginTop: 6, fontSize: 12 }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                    background: myEarnings.target.salary_released ? '#dcfce7' : '#fef9c3',
                    color: myEarnings.target.salary_released ? '#16a34a' : '#a16207',
                  }}>
                    Salary: {myEarnings.target.salary_released ? 'Released ✓' : `Accruing ${myEarnings.target.achievement_percentage || 0}%`}
                  </span>
                </div>
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
